import React from 'react';
import { Select } from 'antd';
import { CaretRight, CaretLeft, CaretDoubleRight, CaretDoubleLeft } from '@phosphor-icons/react';
import { ButtonCommon } from '../Button/ButtonCommon';
import { PaginationProps } from 'types';
/**
 * Reusable pagination component with customizable appearance and behavior
 */
const Pagination: React.FC<PaginationProps> = ({
  current,
  total,
  pageSize,
  pageSizeOptions = ['5', '10', '20', '50', '100'],
  onChange,
  onPageSizeChange,
  showTotal = true,
  totalTextFormat,
  showSizeChanger = true,
  pageSizeText = 'Số lượng hàng trên trang',
  maxVisiblePages = 3,
  className = '',
}) => {
  // Calculate total number of pages
  const totalPages = Math.ceil(total / pageSize);

  // Handle page change
  const handlePageChange = (page: number) => {
    if (onChange && page !== current && page >= 1 && page <= totalPages) {
      onChange(page, pageSize);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (size: string) => {
    if (onPageSizeChange) {
      onPageSizeChange(current, parseInt(size));
    } else if (onChange) {
      // If onPageSizeChange is not provided, use onChange
      const newPage = Math.min(current, Math.ceil(total / parseInt(size)));
      onChange(newPage, parseInt(size));
    }
  };

  // Calculate visible page numbers
  const getPageNumbers = () => {
    const pages = [];

    let startPage = Math.max(1, current - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages && startPage > 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  // Generate total text
  const getTotalText = () => {
    if (totalTextFormat) {
      return totalTextFormat(current, pageSize, total);
    }

    const start = (current - 1) * pageSize + 1;
    const end = Math.min(current * pageSize, total);
    return `Hiển thị ${start} - ${end} của ${total} kết quả`;
  };

  // If there's nothing to paginate, don't render
  if (total <= 0 || (totalPages <= 1 && !showSizeChanger)) {
    return null;
  }

  return (
    <div className={`flex items-center justify-between w-full p-4 ${className}`}>
      {/* Total items display */}
      {showTotal && (
        <div className='flex items-center text-sm text-gray-600'>
          <span>{getTotalText()}</span>
        </div>
      )}

      {/* Page buttons */}
      <div className='flex items-center space-x-1'>
        {/* Jump to first page button */}
        <ButtonCommon
          color='text'
          onClick={() => handlePageChange(1)}
          // className='pagination-button'
          size='sm'
          isDisabled={current === 1}
        >
          <CaretDoubleLeft size={18} />
        </ButtonCommon>
        <ButtonCommon
          color='text'
          onClick={() => handlePageChange(current - 1)}
          // className='pagination-button'
          size='sm'
          isDisabled={current === 1}
        >
          <CaretLeft size={18} />
        </ButtonCommon>
        {/* Previous page button */}

        {/* Numbered page buttons */}
        {getPageNumbers().map((page) => (
          <ButtonCommon
            key={page}
            color={page === current ? 'primary' : 'text'}
            onClick={() => handlePageChange(page)}
            size='md'
            radius='rounded'
          >
            {page}
          </ButtonCommon>
        ))}

        {/* Next page button */}

        <ButtonCommon
          color='text'
          onClick={() => handlePageChange(current + 1)}
          // className='pagination-button'
          size='sm'
          isDisabled={current === totalPages}
        >
          <CaretRight size={18} />
        </ButtonCommon>
        {/* Jump to last page button */}
        <ButtonCommon
          color='text'
          onClick={() => handlePageChange(totalPages)}
          // className='pagination-button'
          size='sm'
          isDisabled={current === totalPages}
        >
          <CaretDoubleRight size={18} />
        </ButtonCommon>
      </div>

      {/* Page size selector */}
      {showSizeChanger && (
        <div className='flex items-center'>
          <span className='mr-2 text-sm'>{pageSizeText}</span>
          <Select
            value={String(pageSize)}
            onChange={handlePageSizeChange}
            options={pageSizeOptions.map((size) => ({ value: size, label: size }))}
            className='w-20'
            size='middle'
            bordered={true}
          />
        </div>
      )}
    </div>
  );
};

export default Pagination;
